import { useEffect, useState } from 'react'

import { Divider, Drawer } from '@mantine/core'
import dayjs from 'dayjs'

import { useMutation, useQuery } from '@redwoodjs/web'

import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  CREATE_AUTOMATED_RULE,
  EDIT_AUTOMATED_RULE,
  GET_AUTOMATED_RULE,
} from 'src/graphql/automatedRules.graphql'

import HoursTag from './components/atoms/HoursTag'
import ScheduleTag from './components/atoms/ScheduleTag'
import AutomatedRuleActions from './components/AutomatedRuleActions'
import { ACTION_KEY_MAP } from './components/AutomatedRuleActions'
import AutomatedRuleConditions, {
  CONDITION_KEY_MAP,
} from './components/AutomatedRuleConditions'
import AutomatedRuleHeader from './components/AutomatedRuleHeader'
import AutomatedRuleOverview from './components/AutomatedRuleOverview'
import AutomatedRuleScheduling from './components/AutomatedRuleScheduling'
import RuleActiveHours from './components/RuleActiveHours'
import {
  cleanRuleActions,
  cleanRuleActiveHours,
  cleanRuleConditions,
} from './utils/ruleInput'
import { STEP_CONDITION_KEY } from './utils/utils'

const RuleDetailsPage = ({
  opened,
  close,
  selectedRuleId,
  setSelectedRule,
  refetchQueries,
  isEditing = false,
  isDuplicating = false,
}) => {
  // QUERIES:
  const { data } = useQuery(GET_AUTOMATED_RULE, {
    variables: {
      automatedRuleId: selectedRuleId,
    },
    skip: !selectedRuleId,
  })

  const selectedRule = data?.automatedRule

  // Company timezone is displayed read-only in the Call Hours editor, so the
  // user knows which zone the hours are interpreted in (matches backend gating).
  const currentCompany = useCurrentCompanyContext()

  const resetScheduling = () => {
    setSelectedFrequency(null)
    setSelectedExecutionLimit(null)
    setSelectedTriggerDelay(null)
    setSelectedTriggerDelayInt(0)
  }

  const handleClose = () => {
    setSelectedRule(null)
    close()
  }

  useEffect(() => {
    if (!selectedRule) return

    // When duplicating, prefill from the source rule but mark the name as a
    // copy so the user can tell the new draft apart from the original.
    setRuleName(
      isDuplicating
        ? `Copy of ${selectedRule.name || 'Untitled Rule'}`
        : selectedRule.name || 'Untitled Rule'
    )
    setSelectedConditions(selectedRule.conditions || [])
    const cleanedActions = selectedRule.actions?.length
      ? selectedRule.actions.map((action) => ({ ...action }))
      : []

    setSelectedActions(cleanedActions)

    setSelectedActiveHours(
      selectedRule.activeHours?.length
        ? selectedRule.activeHours.map((row) => ({ ...row }))
        : []
    )

    setSelectedFrequency(selectedRule.frequency || null)
    setSelectedExecutionLimit(
      selectedRule.executionLimit ? String(selectedRule.executionLimit) : null
    )
    setSelectedTriggerDelay(selectedRule.triggerDelayType || null)
    setSelectedTriggerDelayInt(selectedRule.triggerDelay || 0)

    const hasScheduling =
      selectedRule.frequency ||
      selectedRule.executionLimit ||
      selectedRule.triggerDelayType
    setIsScheduling(!!hasScheduling)
  }, [selectedRule, isDuplicating])

  // MUTATIONS:
  const [editRule, { loading: editRuleLoading }] = useMutation(
    EDIT_AUTOMATED_RULE,
    {
      refetchQueries: refetchQueries,
      onCompleted: () => {
        toast('Your rule was updated successfully', 'success')
        handleClose()
      },
      onError: () => {
        toast('Unable to update rule, please try again.', 'error')
      },
    }
  )

  const [createRule, { loading: createRuleLoading }] = useMutation(
    CREATE_AUTOMATED_RULE,
    {
      refetchQueries: refetchQueries,
      onCompleted: () => {
        toast('A new rule was created successfully', 'success')
        handleClose()
      },
      onError: () => {
        toast('Unable to create rule, please try again.', 'error')
      },
    }
  )

  const onRuleSubmit = () => {
    const cleanedConditions = cleanRuleConditions(selectedConditions, {
      isEditing,
    })
    const cleanedActions = cleanRuleActions(selectedActions, { isEditing })

    const cleanedActiveHours = cleanRuleActiveHours(selectedActiveHours, {
      isEditing,
    })

    const payload = {
      name: ruleName,
      conditions: cleanedConditions,
      actions: cleanedActions,
      activeHours: cleanedActiveHours,
      frequency: null,
      frequencyInterval: null,
      executionLimit: null,
      triggerDelayType: null,
      triggerDelay: null,
    }

    // Always explicitly set trigger delay fields (including when clearing)
    payload.frequency = selectedFrequency
    payload.triggerDelayType = selectedTriggerDelay
    payload.triggerDelay = selectedTriggerDelay ? selectedTriggerDelayInt : null
    payload.executionLimit =
      selectedExecutionLimit != null ? parseInt(selectedExecutionLimit) : null

    if (isEditing) {
      editRule({
        variables: {
          automatedRuleId: selectedRule.automatedRuleId,
          input: payload,
        },
      })
    } else {
      createRule({
        variables: {
          input: payload,
        },
      })
    }
  }

  const [ruleName, setRuleName] = useState(
    'Automated Rule - ' + dayjs().format('MMMM DD, YYYY')
  )

  // Start with no actions so the user explicitly picks one (canSave gates
  // Save until selectedActions.length > 0). Removed the implicit SMS default
  // since it surprised users who forgot to change it before saving.
  const [selectedActions, setSelectedActions] = useState([])

  const [selectedActiveHours, setSelectedActiveHours] = useState([])

  const [selectedConditions, setSelectedConditions] = useState(
    selectedRule?.conditions || []
  )
  const [selectedFrequency, setSelectedFrequency] = useState(null)
  const [selectedExecutionLimit, setSelectedExecutionLimit] = useState(null)
  const [selectedTriggerDelay, setSelectedTriggerDelay] = useState(null)
  const [selectedTriggerDelayInt, setSelectedTriggerDelayInt] = useState(0)
  const [isScheduling, setIsScheduling] = useState(false)
  // Variant C: Schedule lives as a footer-tag at the bottom of the drawer.
  // The form below the tag stays collapsed by default; clicking the tag's
  // "Edit schedule" affordance reveals AutomatedRuleScheduling inline.
  const [scheduleExpanded, setScheduleExpanded] = useState(false)
  // Active hours mirror the schedule tag: collapsed to a "Runs all hours" pill
  // by default, expanded via the "Hour details" box.
  const [hoursExpanded, setHoursExpanded] = useState(false)

  // Validation function to check if scheduling fields are complete
  const isSchedulingComplete = () => {
    if (!isScheduling) return true // If scheduling is disabled, it's always valid

    // Check if all required scheduling fields are filled
    const hasTriggerDelay = selectedTriggerDelay && selectedTriggerDelayInt > 0
    const hasFrequency = selectedFrequency
    const hasExecutionLimit =
      selectedExecutionLimit && Number(selectedExecutionLimit) > 0

    return hasTriggerDelay && hasFrequency && hasExecutionLimit
  }

  // Overall form validation
  const canSave = () => {
    const hasName = ruleName && ruleName.trim().length > 0
    const hasValidActions =
      Array.isArray(selectedActions) &&
      selectedActions.length > 0 &&
      selectedActions.every((action) =>
        ACTION_KEY_MAP[action?.actionType]?.isValidAction(action)
      )

    // Validate that the action has fcraLegalRequirementsConsent if the package type is HireRight
    // If the action contains fcraLegalRequirementsConsent field, check if it's true

    const hasValidFcraConsent = selectedActions.every(
      (action) => action?.fcraLegalRequirementsConsent !== false
    )

    // Has conditions, and every condition is valid:
    // - parent conditions require a value
    // - sub-conditions (e.g. Terminated Reason under Applicant Stage) may be
    //   left empty, meaning "any" — empty rows are stripped before submit
    // - step conditions require both stepType and stepStatus
    const hasConditions =
      Array.isArray(selectedConditions) &&
      selectedConditions.length > 0 &&
      selectedConditions.every((condition) =>
        condition.key === STEP_CONDITION_KEY
          ? condition.stepType && condition.stepStatus
          : condition.value ||
            CONDITION_KEY_MAP[condition.key]?.isParentCondition === false
      )
    const schedulingValid = isSchedulingComplete()
    return (
      hasName &&
      hasValidActions &&
      hasConditions &&
      schedulingValid &&
      hasValidFcraConsent
    )
  }

  const saveDisabled = !canSave()

  return (
    <Drawer
      opened={opened}
      onClose={handleClose}
      position="right"
      withCloseButton={false}
      size={'30%'}
      offset={6}
      radius="md"
      overlayProps={{ backgroundOpacity: 0.2 }}
      classNames={{
        title: 'text-base font-semibold text-doubleNickel-gray-900 ',
        body: 'flex flex-col gap-10 h-full',
      }}
    >
      <div className="flex h-full flex-col gap-4 ">
        <div className="flex h-full flex-1 flex-col gap-3 overflow-y-auto">
          <AutomatedRuleHeader
            ruleName={ruleName}
            isDisabled={saveDisabled}
            isLoading={createRuleLoading || editRuleLoading}
            onRuleSubmit={onRuleSubmit}
          />
          <Divider variant="dashed" />
          <div className="flex flex-col gap-3 overflow-y-auto">
            <AutomatedRuleOverview
              ruleName={ruleName}
              setRuleName={setRuleName}
            />
            <Divider variant="dashed" />
            {/* Reads as one sentence: When (conditions) → Then (actions) →
                Schedule footer-tag — mirrors how the pipeline RuleCard
                presents the same rule. */}
            <AutomatedRuleConditions
              conditions={selectedConditions}
              setConditions={setSelectedConditions}
            />
            <Divider variant="dashed" />
            <AutomatedRuleActions
              actions={selectedActions}
              setActions={setSelectedActions}
            />
            <HoursTag
              activeHours={selectedActiveHours}
              expanded={hoursExpanded}
              onToggleExpand={() => setHoursExpanded((v) => !v)}
            >
              <RuleActiveHours
                activeHours={selectedActiveHours}
                onChange={setSelectedActiveHours}
                timezone={currentCompany?.timezone}
              />
            </HoursTag>
            <ScheduleTag
              triggerDelay={selectedTriggerDelayInt}
              triggerDelayType={selectedTriggerDelay}
              frequency={selectedFrequency}
              frequencyInterval={null}
              executionLimit={
                selectedExecutionLimit
                  ? parseInt(selectedExecutionLimit, 10)
                  : null
              }
              expanded={scheduleExpanded}
              onToggleExpand={() => setScheduleExpanded((v) => !v)}
            >
              <AutomatedRuleScheduling
                isScheduling={isScheduling}
                setIsScheduling={setIsScheduling}
                resetScheduling={resetScheduling}
                selectedExecutionLimit={selectedExecutionLimit}
                setSelectedExecutionLimit={setSelectedExecutionLimit}
                selectedFrequency={selectedFrequency}
                setSelectedFrequency={setSelectedFrequency}
                selectedTriggerDelay={selectedTriggerDelay}
                setSelectedTriggerDelay={setSelectedTriggerDelay}
                selectedTriggerDelayInt={selectedTriggerDelayInt}
                setSelectedTriggerDelayInt={setSelectedTriggerDelayInt}
              />
            </ScheduleTag>
          </div>
        </div>
      </div>
    </Drawer>
  )
}

export default RuleDetailsPage
